import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { IoChevronDown } from "react-icons/io5";
import { twMerge } from "tailwind-merge";
import type { LgvSelectOption } from "../utils/selectOptions";

export interface LgvSelectFieldProps {
  id?: string;
  name?: string;
  value: string;
  options: LgvSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  noOptionsMessage?: string;
}

const LgvSelectField: React.FC<LgvSelectFieldProps> = ({
  id,
  name,
  value,
  options,
  onChange,
  disabled = false,
  required = false,
  placeholder = "Select...",
  className,
  searchable = false,
  searchPlaceholder = "Search...",
  noOptionsMessage = "No options found",
}) => {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const listboxId = `${fieldId}-listbox`;
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedLabel = useMemo(() => {
    if (!value) return "";
    const match = options.find((opt) => opt.value === String(value));
    return match?.label ?? "";
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!searchable || !query) return options;
    return options.filter((opt) => {
      const label = opt.label.toLowerCase();
      const optionValue = opt.value.toLowerCase();
      return label.includes(query) || optionValue.includes(query);
    });
  }, [options, searchable, searchQuery]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setHighlightIndex(-1);
    setSearchQuery("");
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    setOpen(true);
    const selectedIndex = options.findIndex((opt) => opt.value === String(value));
    setHighlightIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [disabled, options, value]);

  useEffect(() => {
    if (!open || !searchable) return;
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [open, searchable]);

  useEffect(() => {
    if (!open) return;
    setHighlightIndex((prev) => {
      if (filteredOptions.length === 0) return -1;
      if (prev < 0 || prev >= filteredOptions.length) return 0;
      return prev;
    });
  }, [filteredOptions.length, open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closeMenu();
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, closeMenu]);

  const selectValue = (nextValue: string) => {
    onChange(nextValue);
    closeMenu();
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (!open) {
      if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
        event.preventDefault();
        openMenu();
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightIndex((prev) =>
        Math.min(prev + 1, filteredOptions.length - 1),
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < filteredOptions.length) {
        selectValue(filteredOptions[highlightIndex].value);
      }
    }
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightIndex((prev) =>
        Math.min(prev + 1, filteredOptions.length - 1),
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      if (highlightIndex >= 0 && highlightIndex < filteredOptions.length) {
        selectValue(filteredOptions[highlightIndex].value);
      }
    }
  };

  return (
    <div ref={containerRef} className={twMerge("relative w-full", className)}>
      <button
        type="button"
        id={fieldId}
        name={name}
        role="combobox"
        disabled={disabled}
        aria-required={required || undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listboxId : undefined}
        aria-autocomplete="none"
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={handleTriggerKeyDown}
        className={twMerge(
          "flex w-full items-center rounded-md border bg-white p-2 text-left transition-colors",
          disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-100 opacity-50"
            : "border-gray-300 hover:border-[#1B365D]/40 focus:outline-none focus:ring-2 focus:ring-blue-500"
        )}
      >
        <span
          className={twMerge(
            "min-w-0 flex-1 truncate",
            selectedLabel ? "text-[#1B365D]" : "text-gray-400"
          )}
        >
          {selectedLabel || placeholder}
        </span>
        <IoChevronDown
          className={twMerge(
            "ml-2 h-5 w-5 shrink-0 text-[#1B365D] transition-transform",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </button>

      {open && !disabled && (
        <ul
          id={listboxId}
          role="listbox"
          aria-labelledby={fieldId}
          className="absolute left-0 top-[calc(100%+4px)] z-50 max-h-60 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-[0px_8px_24px_rgba(27,54,93,0.12)]"
        >
          {searchable && (
            <li role="presentation" className="sticky top-0 z-10 bg-white px-2 pb-2 pt-1">
              <input
                ref={searchInputRef}
                type="search"
                value={searchQuery}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setHighlightIndex(0);
                }}
                onKeyDown={handleSearchKeyDown}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-[#1B365D] outline-none transition-colors placeholder:text-gray-400 focus:border-[#1B365D]/50 focus:ring-2 focus:ring-blue-500"
              />
            </li>
          )}
          <li role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={value === ""}
              onMouseEnter={() => setHighlightIndex(-1)}
              onClick={() => selectValue("")}
              className={twMerge(
                "w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-slate-50",
                value === "" && "bg-slate-50 font-medium"
              )}
            >
              {placeholder}
            </button>
          </li>
          {filteredOptions.length === 0 && (
            <li
              role="presentation"
              className="px-3 py-2 text-sm text-gray-500"
            >
              {noOptionsMessage}
            </li>
          )}
          {filteredOptions.map((opt, index) => {
            const isSelected = opt.value === String(value);
            const isHighlighted = index === highlightIndex;
            return (
              <li key={`${opt.value}-${index}`} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setHighlightIndex(index)}
                  onClick={() => selectValue(opt.value)}
                  className={twMerge(
                    "w-full px-3 py-2 text-left text-sm text-[#1B365D] hover:bg-slate-50",
                    (isSelected || isHighlighted) &&
                      "bg-[#1B365D] text-white hover:bg-[#152a4a]"
                  )}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LgvSelectField;
